import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";

/**
 * Haptics Service
 * 
 * Provides haptic feedback (vibration) for user interactions.
 * Works on native platforms (iOS/Android) and gracefully degrades on web.
 * 
 * Usage:
 * - Light feedback: this.hapticsService.light()
 * - Medium feedback: this.hapticsService.medium()
 * - Heavy feedback: this.hapticsService.heavy()
 * - Selection: this.hapticsService.selection()
 * - Success: this.hapticsService.success()
 * - Warning: this.hapticsService.warning()
 * - Error: this.hapticsService.error()
 */
@Injectable({
  providedIn: "root",
})
export class HapticsService {
  private isAvailable: boolean = false;

  constructor() {
    this.isAvailable = Capacitor.isNativePlatform();
  }

  /**
   * Check if haptics are available on the current platform
   */
  isHapticsAvailable(): boolean {
    return this.isAvailable;
  }

  /**
   * Light impact feedback
   * Use for subtle interactions like button taps, list item selections
   */
  async light(): Promise<void> {
    if (!this.isAvailable) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
      console.warn("[Haptics] Light impact failed:", error);
    }
  }

  /**
   * Medium impact feedback
   * Use for standard interactions like button presses, card taps
   */
  async medium(): Promise<void> {
    if (!this.isAvailable) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (error) {
      console.warn("[Haptics] Medium impact failed:", error);
    }
  }

  /**
   * Heavy impact feedback
   * Use for important actions like confirmations, deletions, critical actions
   */
  async heavy(): Promise<void> {
    if (!this.isAvailable) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (error) {
      console.warn("[Haptics] Heavy impact failed:", error);
    }
  }

  /**
   * Selection feedback
   * Use for picker selections, toggle switches, segmented controls
   */
  async selection(): Promise<void> {
    if (!this.isAvailable) return;
    try {
      await Haptics.selectionStart();
      // Small delay to make selection feel more natural
      setTimeout(async () => {
        try {
          await Haptics.selectionChanged();
        } catch (err) {
          // Ignore errors in delayed call
        }
      }, 10);
    } catch (error) {
      console.warn("[Haptics] Selection feedback failed:", error);
    }
  }

  /**
   * Selection start feedback
   * Call when user starts interacting with a picker/selector
   */
  async selectionStart(): Promise<void> {
    if (!this.isAvailable) return;
    try {
      await Haptics.selectionStart();
    } catch (error) {
      console.warn("[Haptics] Selection start failed:", error);
    }
  }

  /**
   * Selection changed feedback
   * Call when selection value changes
   */
  async selectionChanged(): Promise<void> {
    if (!this.isAvailable) return;
    try {
      await Haptics.selectionChanged();
    } catch (error) {
      console.warn("[Haptics] Selection changed failed:", error);
    }
  }

  /**
   * Selection end feedback
   * Call when user finishes selecting
   */
  async selectionEnd(): Promise<void> {
    if (!this.isAvailable) return;
    try {
      await Haptics.selectionEnd();
    } catch (error) {
      console.warn("[Haptics] Selection end failed:", error);
    }
  }

  /**
   * Success notification feedback
   * Use for successful operations, confirmations, completions
   */
  async success(): Promise<void> {
    if (!this.isAvailable) return;
    try {
      await Haptics.notification({ type: NotificationType.Success });
    } catch (error) {
      console.warn("[Haptics] Success notification failed:", error);
    }
  }

  /**
   * Warning notification feedback
   * Use for warnings, cautions, important notices
   */
  async warning(): Promise<void> {
    if (!this.isAvailable) return;
    try {
      await Haptics.notification({ type: NotificationType.Warning });
    } catch (error) {
      console.warn("[Haptics] Warning notification failed:", error);
    }
  }

  /**
   * Error notification feedback
   * Use for errors, failures, invalid operations
   */
  async error(): Promise<void> {
    if (!this.isAvailable) return;
    try {
      await Haptics.notification({ type: NotificationType.Error });
    } catch (error) {
      console.warn("[Haptics] Error notification failed:", error);
    }
  }

  /**
   * Custom vibration
   * Use for custom duration vibrations (Android primarily)
   * @param duration Duration in milliseconds (default: 2500ms)
   */
  async vibrate(duration: number = 2500): Promise<void> {
    if (!this.isAvailable) return;
    try {
      await Haptics.vibrate({ duration });
    } catch (error) {
      console.warn("[Haptics] Vibrate failed:", error);
    }
  }

  /**
   * Button press feedback
   * Convenience method for button interactions
   */
  async buttonPress(): Promise<void> {
    await this.medium();
  }

  /**
   * Long press feedback
   * Convenience method for long press interactions
   */
  async longPress(): Promise<void> {
    await this.heavy();
  }

  /**
   * Swipe feedback
   * Convenience method for swipe gestures
   */
  async swipe(): Promise<void> {
    await this.light();
  }

  /**
   * Pull to refresh feedback
   * Convenience method for pull-to-refresh actions
   */
  async pullToRefresh(): Promise<void> {
    await this.medium();
  }

  /**
   * Toggle switch feedback
   * Convenience method for toggle switches
   */
  async toggle(): Promise<void> {
    await this.selection();
  }

  /**
   * Form validation feedback
   * Use when form validation fails
   */
  async validationError(): Promise<void> {
    await this.error();
  }

  /**
   * Form submission success feedback
   * Use when form is successfully submitted
   */
  async formSuccess(): Promise<void> {
    await this.success();
  }
}

